import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Mail, Send, Settings, CheckCircle, AlertCircle, Bell } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EmailSettingsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [config, setConfig] = useState({
    notification_email: '',
    resend_api_key: '',
    sender_email: '',
    surgery_reminder_days: 1,
    enabled: false
  });
  
  const [hasApiKey, setHasApiKey] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig({
        notification_email: response.data.notification_email || '',
        resend_api_key: '',
        sender_email: response.data.sender_email || '',
        surgery_reminder_days: response.data.surgery_reminder_days || 1,
        enabled: response.data.enabled || false
      });
      setHasApiKey(response.data.has_api_key);
      setTestEmail(response.data.notification_email || '');
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!config.notification_email) {
      toast.error('El email de notificación es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/email/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
      setHasApiKey(!!config.resend_api_key || hasApiKey);
      setConfig(prev => ({ ...prev, resend_api_key: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Ingresa un email para la prueba');
      return;
    }
    
    setTesting(true);
    try {
      await axios.post(`${API}/email/test`, {
        recipient_email: testEmail,
        subject: 'Prueba de configuración - MedVoice'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Configuración de Email</h1>
          <p className="text-slate-500 mt-1">Configura las notificaciones por correo electrónico</p>
        </div>
        <Badge variant={hasApiKey && config.enabled ? 'default' : 'secondary'} className="gap-1">
          {hasApiKey && config.enabled ? (
            <><CheckCircle className="w-3 h-3" /> Activo</>
          ) : (
            <><AlertCircle className="w-3 h-3" /> Inactivo</>
          )}
        </Badge>
      </div>

      {/* API Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            Configuración de Resend
          </CardTitle>
          <CardDescription>
            Obtén tu API Key en <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-600 underline">resend.com</a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>API Key de Resend</Label>
              <Input
                type="password"
                value={config.resend_api_key}
                onChange={(e) => setConfig(prev => ({ ...prev, resend_api_key: e.target.value }))}
                placeholder={hasApiKey ? '••••••••••••••••' : 're_xxxxxxxxxx'}
              />
              {hasApiKey && (
                <p className="text-xs text-green-600">✓ API Key configurada</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email del remitente (opcional)</Label>
              <Input
                type="email"
                value={config.sender_email}
                onChange={(e) => setConfig(prev => ({ ...prev, sender_email: e.target.value }))}
                placeholder="noreply@tudominio.com"
              />
              <p className="text-xs text-slate-500">Por defecto: onboarding@resend.dev</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Notificaciones
          </CardTitle>
          <CardDescription>
            Configura el email donde recibirás los recordatorios de cirugías
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email de notificación *</Label>
              <Input
                type="email"
                value={config.notification_email}
                onChange={(e) => setConfig(prev => ({ ...prev, notification_email: e.target.value }))}
                placeholder="tu@email.com"
              />
              <p className="text-xs text-slate-500">Aquí llegarán los recordatorios de cirugías</p>
            </div>
            <div className="space-y-2">
              <Label>Días de anticipación para recordatorios</Label>
              <Input
                type="number"
                min="1"
                max="7"
                value={config.surgery_reminder_days}
                onChange={(e) => setConfig(prev => ({ ...prev, surgery_reminder_days: parseInt(e.target.value) || 1 }))}
              />
              <p className="text-xs text-slate-500">Cuántos días antes de la cirugía enviar el recordatorio</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-800">Activar notificaciones por email</p>
              <p className="text-sm text-slate-500">Recibe recordatorios de cirugías programadas</p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Test Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Enviar email de prueba
          </CardTitle>
          <CardDescription>
            Verifica que la configuración funciona correctamente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="email@prueba.com"
              className="flex-1"
            />
            <Button 
              onClick={handleSendTest} 
              disabled={testing || !hasApiKey}
              variant="outline"
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              {testing ? 'Enviando...' : 'Enviar prueba'}
            </Button>
          </div>
          {!hasApiKey && (
            <p className="text-xs text-amber-600 mt-2">
              Guarda primero la API Key para poder enviar emails de prueba
            </p>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
